import { Machine, get_preferred_connection, probe_local_machine, OfflineMachine, LocalHostConnection, RemoteConnection, InsecureConnection, ProxyConnection, IConnection } from './Connections';
import { versionCompare } from './Common';

export interface RefreshResult {
    onlineMachines: Machine[];
    localMachines: Machine[];
    offlineMachines: OfflineMachine[];
}

const MY_HOST = import.meta.env.VITE_MY_HOST || 'https://spectre-licensing.com';
export const ONLINE_MACHINES_KEY = 'onlineMachines';
export const LOCAL_MACHINES_KEY = 'localMachines';
const UPDATE_REQUIRED = import.meta.env.VITE_UPDATE_REQUIRED;

// Function to check if a machine version is outdated
export function isMachineOutdated(machineVersion: string): boolean {
    if (!UPDATE_REQUIRED || !machineVersion) return false;
    // versionCompare returns 1 if v1 > v2, 0 if equal, -1 if v1 < v2
    // We consider the machine outdated if machineVersion < UPDATE_REQUIRED
    return versionCompare(machineVersion, UPDATE_REQUIRED, {}) === -1;
};

// Function to recreate connection from cached connection type
export function recreateConnectionFromType(connectionType: string, machineData: any, cookies: string): IConnection {
    switch (connectionType) {
        case 'LocalHost':
            return new LocalHostConnection(machineData);
        case 'Remote':
            return new RemoteConnection(machineData);
        case 'Insecure':
            return new InsecureConnection(machineData);
        case 'Proxy':
            return new ProxyConnection(machineData, cookies);
        default:
            throw new Error(`Unknown connection type: ${connectionType}`);
    }
}

export async function refreshMatchmakingData(cookiesUser64: string, signal?: AbortSignal): Promise<RefreshResult> {
    // Fetch match list
    const response = await fetch(`${MY_HOST}/api/matchmaking/list`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        signal,
    });
    if (!response.ok) throw new Error(`Matchmaking list failed: ${response.status}`);

    const data = await response.json();
    // A user with no licensed machines gets an empty (or absent) list. That must NOT stop us
    // from still probing localhost — a brand-new machine that hasn't checked out a license yet
    // won't be in the list but can still be reached on localhost for first-time sign-in.
    const matches: Machine[] = Array.isArray(data?.matches) ? data.matches : [];

    // Cache raw match objects by machine name like existing flow
    matches.forEach((machine: Machine) => localStorage.setItem(machine.machineName, JSON.stringify(machine)));

    // Probe localhost once to identify the machine running the local GUI. Kicked off
    // concurrently with the per-machine resolution below so it never stalls remote probing.
    const localIdentityPromise = probe_local_machine(matches);

    const results: Array<{ machine: Machine; conn: IConnection | null }> = await Promise.all(
        matches.map(async (machine: Machine) => {
            try {
                // Only one machine can be local, so skip the localhost probe for every machine here.
                const conn = await get_preferred_connection(machine, cookiesUser64, 3, 500, false);
                return { machine, conn };
            } catch {
                return { machine, conn: null };
            }
        })
    );

    // Reconcile the local machine: whichever match's device ID equals the localhost ID gets a
    // direct LocalHostConnection, even if its remote/proxy probe failed.
    const localIdentity = await localIdentityPromise;
    if (localIdentity) {
        const local = results.find((r) => (r.machine.serialNumber ?? r.machine.macAddress) === localIdentity.id);
        if (local) {
            // Known machine: upgrade it to a direct LocalHostConnection.
            if (localIdentity.version) local.machine.version = localIdentity.version;
            local.conn = new LocalHostConnection(local.machine);
        } else {
            // Local GUI is online but not in the matchmaking list (e.g. a brand-new machine with
            // no license checked out yet). Supplement results with a minimal machine built from
            // what the localhost probe told us so it can still be shown and offered for sign-in.
            const syntheticMachine = {
                machineName: localIdentity.id,
                localIP: `localhost:${localIdentity.port}`,
                publicIP: '',
                matchmakingID: -1,
                location: null,
                macAddress: '',
                scanMode: 0,
                certification: null,
                version: localIdentity.version,
                serialNumber: localIdentity.id,
            } as unknown as Machine;
            const localConn = new LocalHostConnection(syntheticMachine);
            results.push({ machine: syntheticMachine, conn: localConn });
        }
    }

    const nextOnline: Machine[] = [];
    const nextLocal: Machine[] = [];
    const nextOffline: OfflineMachine[] = [];

    results.forEach(({ machine: m, conn }) => {
        if (!conn) {
            const offlineMachine = new OfflineMachine(
                m.machineName,
                m.localIP,
                m.publicIP,
                m.matchmakingID,
                m.location,
                m.macAddress,
                m.scanMode,
                m.version,
                m.serialNumber,
                isMachineOutdated(m.version)
            );
            
            // Add isOutdated property to the offline machine object
            offlineMachine.isOutdated = isMachineOutdated(m.version);
            
            nextOffline.push(offlineMachine);
            return;
        }
        const machineObj = new Machine(
            conn,
            m.machineName,
            m.localIP,
            m.publicIP,
            m.matchmakingID,
            m.location,
            m.macAddress,
            m.scanMode,
            m.certification,
            m.version,
            m.serialNumber,
            isMachineOutdated(m.version)
        );
        
        if (conn instanceof LocalHostConnection) nextLocal.push(machineObj);
        nextOnline.push(machineObj);
    });

    // For local machines, first check whether the machine supports the login API
    // (feature-flag gated). Only if it does do we bother calling isLicensed, since the
    // login modal is never shown for machines without login support anyway.
    await Promise.all(
        nextLocal.map(async (m) => {
            if (m.connection instanceof LocalHostConnection) {
                const loginApiSupported = await m.connection.supportsLoginAPI();
                m.supportsLoginAPI = loginApiSupported;
                m.isLicensed = loginApiSupported ? await m.connection.isLicensed() : undefined;
            }
        })
    );

    // Save to localStorage for reuse
    localStorage.setItem(
        ONLINE_MACHINES_KEY,
        JSON.stringify(
            nextOnline.map((m) => ({
                machineName: m.machineName,
                localIP: m.localIP,
                publicIP: m.publicIP,
                matchmakingID: m.matchmakingID,
                location: m.location,
                macAddress: m.macAddress,
                scanMode: m.scanMode,
                certification: m.certification,
                version: m.version,
                serialNumber: m.serialNumber,
                connectionType: (m as Machine).connection.type(),
                isOutdated: m.isOutdated
            }))
        )
    );
    localStorage.setItem(
        LOCAL_MACHINES_KEY,
        JSON.stringify(
            nextLocal.map((m) => ({
                machineName: m.machineName,
                localIP: m.localIP,
                publicIP: m.publicIP,
                matchmakingID: m.matchmakingID,
                location: m.location,
                macAddress: m.macAddress,
                scanMode: m.scanMode,
                certification: m.certification,
                version: m.version,
                serialNumber: m.serialNumber,
                connectionType: (m as Machine).connection.type(),
                isOutdated: m.isOutdated
            }))
        )
    );

    return { onlineMachines: nextOnline, localMachines: nextLocal, offlineMachines: nextOffline };
}
